import { create } from 'zustand';
import { api, tokenStore } from '@/shared/api/client';
import type { AuthUser, TokenPair } from '@/shared/api/types';

interface AuthState {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => void;
  loadMe: () => Promise<void>;
}

interface RegisterInput {
  organizationName: string;
  email: string;
  displayName: string;
  password: string;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',

  login: async (email, password) => {
    const res = await api<TokenPair>('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: { email, password },
    });
    tokenStore.set(res.accessToken, res.refreshToken);
    set({ user: res.user, status: 'authenticated' });
  },

  register: async (input) => {
    const res = await api<TokenPair>('/api/auth/register', {
      method: 'POST',
      auth: false,
      body: input,
    });
    tokenStore.set(res.accessToken, res.refreshToken);
    set({ user: res.user, status: 'authenticated' });
  },

  logout: () => {
    tokenStore.clear();
    set({ user: null, status: 'anonymous' });
  },

  loadMe: async () => {
    if (!tokenStore.access) {
      set({ status: 'anonymous' });
      return;
    }
    try {
      const user = await api<AuthUser>('/api/auth/me');
      set({ user, status: 'authenticated' });
    } catch {
      tokenStore.clear();
      set({ user: null, status: 'anonymous' });
    }
  },
}));
